import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import StatCard from "../components/common/StatCard";
import { downloadApiFile } from "../utils/download";
import { getWorkplanYears } from "../utils/workplanYears";

const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const STATUS_OPTIONS = ["Completed", "In Progress", "On Hold", "Not Done"];
const MONTH_OPTIONS = [
  { label: "January", value: "January", quarter: "Q1" },
  { label: "February", value: "February", quarter: "Q1" },
  { label: "March", value: "March", quarter: "Q1" },
  { label: "April", value: "April", quarter: "Q2" },
  { label: "May", value: "May", quarter: "Q2" },
  { label: "June", value: "June", quarter: "Q2" },
  { label: "July", value: "July", quarter: "Q3" },
  { label: "August", value: "August", quarter: "Q3" },
  { label: "September", value: "September", quarter: "Q3" },
  { label: "October", value: "October", quarter: "Q4" },
  { label: "November", value: "November", quarter: "Q4" },
  { label: "December", value: "December", quarter: "Q4" }
];
const QUARTER_COLORS = {
  Q1: "#1f6f78",
  Q2: "#d97706",
  Q3: "#4f46e5",
  Q4: "#be185d"
};
const STATUS_COLORS = {
  Completed: "#1f6f78",
  "In Progress": "#d97706",
  "On Hold": "#4f46e5",
  "Not Done": "#be123c"
};

function shortenChartLabel(value, maxLength = 24) {
  if (!value) {
    return "";
  }

  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

function formatInteger(value) {
  return Number(value || 0).toLocaleString();
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatCompact(value) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(Number(value || 0));
}

function safeShortLabel(value, maxLength = 24) {
  if (!value) {
    return "";
  }

  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function normalizeActivityStatusLabel(status) {
  return status === "Cancelled" ? "Not Done" : status;
}

function sanitizeChartFileName(value) {
  return String(value || "chart")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function DashboardPage() {
  const currentYear = new Date().getFullYear();
  const availableYears = getWorkplanYears(currentYear);
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState("");
  const [month, setMonth] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const chartRefs = useRef({});

  useEffect(() => {
    fetchStats();
  }, [year, quarter, month]);

  useEffect(() => {
    if (month && quarter) {
      const selectedMonth = MONTH_OPTIONS.find((item) => item.value === month);
      if (selectedMonth && selectedMonth.quarter !== quarter) {
        setMonth("");
      }
    }
  }, [quarter, month]);

  async function fetchStats() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (quarter) {
        params.set("quarter", quarter);
      }
      if (month) {
        params.set("month", month);
      }

      const queryString = params.toString();
      const response = await api.get(
        `/activities/dashboard/year/${year}${queryString ? `?${queryString}` : ""}`
      );
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load dashboard statistics.");
    } finally {
      setLoading(false);
    }
  }

  function handleClearFilters() {
    setYear(currentYear);
    setQuarter("");
    setMonth("");
  }

  async function handleDownloadActiveTab() {
    setDownloading(true);

    try {
      await downloadApiFile(`/activities/dashboard/year/${year}/export`, {
        params: {
          tab: activeTab,
          quarter: quarter || undefined,
          month: month || undefined
        },
        fallbackFileName: `dashboard-${activeTab}-${year}.csv`
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download dashboard export.");
    } finally {
      setDownloading(false);
    }
  }

  function setChartRef(chartKey, node) {
    chartRefs.current[chartKey] = node;
  }

  function getChartSvg(chartKey) {
    const container = chartRefs.current[chartKey];
    if (!container) {
      return null;
    }

    return container.querySelector("svg");
  }

  function triggerFileDownload(blob, fileName) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  function downloadChartSvg(chartKey, title) {
    const svg = getChartSvg(chartKey);
    if (!svg) {
      toast.error("Chart is not ready to download yet.");
      return;
    }

    const serializer = new XMLSerializer();
    const svgMarkup = serializer.serializeToString(svg);
    const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    triggerFileDownload(blob, `${sanitizeChartFileName(title)}.svg`);
  }

  async function downloadChartPng(chartKey, title) {
    const svg = getChartSvg(chartKey);
    if (!svg) {
      toast.error("Chart is not ready to download yet.");
      return;
    }

    const serializer = new XMLSerializer();
    const svgMarkup = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = window.URL.createObjectURL(svgBlob);

    try {
      const image = new Image();
      const imageLoaded = new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });
      image.src = svgUrl;
      await imageLoaded;

      const width = svg.viewBox?.baseVal?.width || svg.getBoundingClientRect().width || 1200;
      const height = svg.viewBox?.baseVal?.height || svg.getBoundingClientRect().height || 700;
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;

      const context = canvas.getContext("2d");
      if (!context) {
        toast.error("PNG export is not supported in this browser.");
        return;
      }

      context.scale(scale, scale);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Failed to create PNG export.");
          return;
        }

        triggerFileDownload(blob, `${sanitizeChartFileName(title)}.png`);
      }, "image/png");
    } catch (err) {
      toast.error("Failed to download chart image.");
    } finally {
      window.URL.revokeObjectURL(svgUrl);
    }
  }

  function renderChartActions(chartKey, title) {
    return (
      <div className="d-flex gap-2">
        <Button variant="outline-secondary" size="sm" onClick={() => downloadChartPng(chartKey, title)}>
          PNG
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={() => downloadChartSvg(chartKey, title)}>
          SVG
        </Button>
      </div>
    );
  }

  function buildQuarterRows(items, valueKey) {
    const grouped = new Map();

    (items || []).forEach((item, index) => {
      const key = item.uuid || item.name || `item-${index}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: item.name || "Unassigned",
          Q1: 0,
          Q2: 0,
          Q3: 0,
          Q4: 0,
          total: 0
        });
      }

      const value = Number(item[valueKey] || 0);
      if (typeof item.total_unique_amount === "number") {
        grouped.get(key).total = Number(item.total_unique_amount || 0);
      }
      if (typeof item.total_unique_activities === "number") {
        grouped.get(key).total = Number(item.total_unique_activities || 0);
      }
      if (QUARTER_OPTIONS.includes(item.quarter)) {
        grouped.get(key)[item.quarter] += value;
        if (typeof item.total_unique_activities !== "number" && typeof item.total_unique_amount !== "number") {
          grouped.get(key).total += value;
        }
      }
    });

    return Array.from(grouped.values()).sort((a, b) => b.total - a.total);
  }

  function buildStatusRows(items) {
    const grouped = new Map();

    STATUS_OPTIONS.forEach((status) => {
      grouped.set(status, {
        key: status,
        name: status,
        Q1: 0,
        Q2: 0,
        Q3: 0,
        Q4: 0,
        total: 0
      });
    });

    (items || []).forEach((item) => {
      const status = normalizeActivityStatusLabel(item.status || "");
      if (!STATUS_OPTIONS.includes(status)) {
        return;
      }
      if (!grouped.has(status)) {
        grouped.set(status, {
          key: status,
          name: status,
          Q1: 0,
          Q2: 0,
          Q3: 0,
          Q4: 0,
          total: 0
        });
      }

      const value = Number(item.total_schedules || 0);
      if (QUARTER_OPTIONS.includes(item.quarter)) {
        grouped.get(status)[item.quarter] += value;
        grouped.get(status).total += value;
      }
    });

    return Array.from(grouped.values());
  }

  function buildGroupedStatusRows(items) {
    const grouped = new Map();

    (items || []).forEach((item, index) => {
      const key = item.uuid || item.name || `item-${index}`;
      const status = normalizeActivityStatusLabel(item.status || "");
      if (!STATUS_OPTIONS.includes(status)) {
        return;
      }

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: item.name || "Unassigned",
          Completed: 0,
          "In Progress": 0,
          "On Hold": 0,
          "Not Done": 0,
          total: 0
        });
      }

      const value = Number(item.total_schedules || 0);
      if (STATUS_OPTIONS.includes(status)) {
        grouped.get(key)[status] += value;
        if (typeof item.total_unique_schedules === "number") {
          grouped.get(key).total = Number(item.total_unique_schedules || 0);
        } else {
          grouped.get(key).total += value;
        }
      }
    });

    return Array.from(grouped.values()).sort((a, b) => b.total - a.total);
  }

  const availableMonths = useMemo(() => {
    if (!quarter) {
      return MONTH_OPTIONS;
    }

    return MONTH_OPTIONS.filter((item) => item.quarter === quarter);
  }, [quarter]);

  function buildSummary(rows) {
    return rows.reduce(
      (totals, item) => ({
        Q1: totals.Q1 + item.Q1,
        Q2: totals.Q2 + item.Q2,
        Q3: totals.Q3 + item.Q3,
        Q4: totals.Q4 + item.Q4,
        total: totals.total + item.total
      }),
      { Q1: 0, Q2: 0, Q3: 0, Q4: 0, total: 0 }
    );
  }

  function renderStackedQuarterChart(chartKey, rows, title, subtitle, options = {}) {
    const resolvedOptions = typeof subtitle === "object" && subtitle !== null ? subtitle : options;

    if (!rows.length) {
      return (
        <div className="content-card chart-card h-100">
          <h5 className="mb-2">{title}</h5>
          <p className="text-muted mt-3 mb-0">No records yet for this filter set.</p>
        </div>
      );
    }

    const maxTotal = Math.max(...rows.map((item) => item.total), 0);
    const {
      valueFormatter = formatInteger,
      axisFormatter = formatInteger,
      labelMaxLength = 16,
      chartHeight = 320
    } = resolvedOptions;

    return (
      <div className="content-card chart-card h-100">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
          <h5 className="mb-0">{title}</h5>
          {renderChartActions(chartKey, title)}
        </div>
        <div ref={(node) => setChartRef(chartKey, node)}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              margin={{ top: 6, right: 18, left: 4, bottom: 12 }}
              barCategoryGap="18%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eadfce" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                tickFormatter={(value) => safeShortLabel(value, labelMaxLength)}
                tick={{ fontSize: 11 }}
                height={56}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, Math.max(5, Math.ceil(maxTotal * 1.15))]}
                tickFormatter={axisFormatter}
              />
              <Tooltip
                formatter={(value, name) => [valueFormatter(value), name]}
                labelFormatter={(label) => label}
                contentStyle={{
                  borderRadius: 14,
                  border: "1px solid #e5dccb",
                  boxShadow: "0 14px 28px rgba(23, 50, 77, 0.12)"
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 10 }} />
              {QUARTER_OPTIONS.map((itemQuarter) => (
                <Bar
                  key={itemQuarter}
                  dataKey={itemQuarter}
                  stackId="quarters"
                  fill={QUARTER_COLORS[itemQuarter]}
                  maxBarSize={56}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  function renderSummaryTable(rows, title, valueFormatter = formatInteger) {
    if (!rows.length) {
      return (
        <div className="content-card table-card h-100">
          <h5 className="mb-2">{title}</h5>
          <p className="text-muted mb-0">No records yet for this filter set.</p>
        </div>
      );
    }

    const summary = buildSummary(rows);

    return (
      <div className="content-card table-card h-100">
        <h5 className="mb-3">{title}</h5>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Q1</th>
                <th>Q2</th>
                <th>Q3</th>
                <th>Q4</th>
                <th>Total</th>
                <th>% Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.key}>
                  <td>{item.name}</td>
                  <td>{valueFormatter(item.Q1)}</td>
                  <td>{valueFormatter(item.Q2)}</td>
                  <td>{valueFormatter(item.Q3)}</td>
                  <td>{valueFormatter(item.Q4)}</td>
                  <td className="fw-semibold">{valueFormatter(item.total)}</td>
                  <td>{summary.total ? formatPercent((item.total / summary.total) * 100) : "0%"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="summary-row">
                <th>Total</th>
                <th>{valueFormatter(summary.Q1)}</th>
                <th>{valueFormatter(summary.Q2)}</th>
                <th>{valueFormatter(summary.Q3)}</th>
                <th>{valueFormatter(summary.Q4)}</th>
                <th>{valueFormatter(summary.total)}</th>
                <th>100%</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  function buildStatusSummary(rows) {
    return rows.reduce(
      (totals, item) => ({
        Completed: totals.Completed + (item.Completed || 0),
        "In Progress": totals["In Progress"] + (item["In Progress"] || 0),
        "On Hold": totals["On Hold"] + (item["On Hold"] || 0),
        "Not Done": totals["Not Done"] + (item["Not Done"] || 0),
        total: totals.total + item.total
      }),
      { Completed: 0, "In Progress": 0, "On Hold": 0, "Not Done": 0, total: 0 }
    );
  }

  function renderStackedStatusChart(chartKey, rows, title, subtitle, options = {}) {
    if (!rows.length) {
      return (
        <div className="content-card chart-card h-100">
          <h5 className="mb-2">{title}</h5>
          <p className="text-muted mt-3 mb-0">No records yet for this filter set.</p>
        </div>
      );
    }

    const maxTotal = Math.max(...rows.map((item) => item.total), 0);
    const {
      valueFormatter = formatInteger,
      axisFormatter = formatInteger,
      labelMaxLength = 16,
      chartHeight = 320
    } = options;

    return (
      <div className="content-card chart-card h-100">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
          <h5 className="mb-0">{title}</h5>
          {renderChartActions(chartKey, title)}
        </div>
        <div ref={(node) => setChartRef(chartKey, node)}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              margin={{ top: 6, right: 18, left: 4, bottom: 12 }}
              barCategoryGap="18%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eadfce" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                tickFormatter={(value) => safeShortLabel(value, labelMaxLength)}
                tick={{ fontSize: 11 }}
                height={56}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, Math.max(5, Math.ceil(maxTotal * 1.15))]}
                tickFormatter={axisFormatter}
              />
              <Tooltip
                formatter={(value, name) => [valueFormatter(value), name]}
                labelFormatter={(label) => label}
                contentStyle={{
                  borderRadius: 14,
                  border: "1px solid #e5dccb",
                  boxShadow: "0 14px 28px rgba(23, 50, 77, 0.12)"
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 10 }} />
              {STATUS_OPTIONS.map((status) => (
                <Bar
                  key={status}
                  dataKey={status}
                  stackId="statuses"
                  fill={STATUS_COLORS[status]}
                  maxBarSize={56}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  function renderStatusSummaryTable(rows, title, valueFormatter = formatInteger) {
    if (!rows.length) {
      return (
        <div className="content-card table-card h-100">
          <h5 className="mb-2">{title}</h5>
          <p className="text-muted mb-0">No records yet for this filter set.</p>
        </div>
      );
    }

    const summary = buildStatusSummary(rows);

    return (
      <div className="content-card table-card h-100">
        <h5 className="mb-3">{title}</h5>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Completed</th>
                <th>In Progress</th>
                <th>On Hold</th>
                <th>Not Done</th>
                <th>Total</th>
                <th>% Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.key}>
                  <td>{item.name}</td>
                  <td>{valueFormatter(item.Completed)}</td>
                  <td>{valueFormatter(item["In Progress"])}</td>
                  <td>{valueFormatter(item["On Hold"])}</td>
                  <td>{valueFormatter(item["Not Done"])}</td>
                  <td className="fw-semibold">{valueFormatter(item.total)}</td>
                  <td>{summary.total ? formatPercent((item.total / summary.total) * 100) : "0%"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="summary-row">
                <th>Total</th>
                <th>{valueFormatter(summary.Completed)}</th>
                <th>{valueFormatter(summary["In Progress"])}</th>
                <th>{valueFormatter(summary["On Hold"])}</th>
                <th>{valueFormatter(summary["Not Done"])}</th>
                <th>{valueFormatter(summary.total)}</th>
                <th>100%</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  const overviewRows = {
    pillar: buildQuarterRows(stats?.by_pillar_quarter, "total_activities"),
    implementor: buildQuarterRows(stats?.by_implementor_quarter, "total_activities"),
    grant: buildQuarterRows(stats?.by_grant_quarter, "total_activities")
  };
  const overviewImplementorCards = (stats?.by_implementor_total || []).map((item, index) => ({
    key: item.uuid || item.name || `implementor-${index}`,
    name: item.name || "Unassigned",
    total: Number(item.total_activities || 0)
  }));

  const budgetRows = {
    pillar: buildQuarterRows(stats?.budget?.by_pillar_quarter, "amount"),
    implementor: buildQuarterRows(stats?.budget?.by_implementor_quarter, "amount"),
    grant: buildQuarterRows(stats?.budget?.by_grant_quarter, "amount")
  };
  const budgetImplementorCards = budgetRows.implementor.map((item, index) => ({
    key: item.key || `budget-implementor-${index}`,
    name: item.name || "Unassigned",
    total: Number(item.total || 0)
  }));

  const completionRows = buildStatusRows(stats?.completion?.by_status_quarter);

  const completionGroupRows = {
    pillar: buildGroupedStatusRows(stats?.completion?.by_pillar_quarter),
    implementor: buildGroupedStatusRows(stats?.completion?.by_implementor_quarter),
    grant: buildGroupedStatusRows(stats?.completion?.by_grant_quarter)
  };

  const absorptionRows = {
    pillar: buildQuarterRows(stats?.funds_absorption?.by_pillar_quarter, "amount"),
    implementor: buildQuarterRows(stats?.funds_absorption?.by_implementor_quarter, "amount"),
    grant: buildQuarterRows(stats?.funds_absorption?.by_grant_quarter, "amount")
  };

  const accountedRows = {
    pillar: buildQuarterRows(stats?.funds_absorption?.by_pillar_accounted_quarter, "amount"),
    implementor: buildQuarterRows(stats?.funds_absorption?.by_implementor_accounted_quarter, "amount"),
    grant: buildQuarterRows(stats?.funds_absorption?.by_grant_accounted_quarter, "amount")
  };

  const balanceRows = {
    pillar: buildQuarterRows(stats?.funds_absorption?.by_pillar_balance_quarter, "amount"),
    implementor: buildQuarterRows(stats?.funds_absorption?.by_implementor_balance_quarter, "amount"),
    grant: buildQuarterRows(stats?.funds_absorption?.by_grant_balance_quarter, "amount")
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        action={
          <div className="dashboard-filters">
            <Form.Select
              className="dashboard-filter"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
            >
              {availableYears.map((itemYear) => (
                <option key={itemYear} value={itemYear}>
                  {itemYear}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="dashboard-filter"
              value={quarter}
              onChange={(event) => setQuarter(event.target.value)}
            >
              <option value="">All quarters</option>
              {QUARTER_OPTIONS.map((itemQuarter) => (
                <option key={itemQuarter} value={itemQuarter}>
                  {itemQuarter}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="dashboard-filter"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
            >
              <option value="">All months</option>
              {availableMonths.map((itemMonth) => (
                <option key={itemMonth.value} value={itemMonth.value}>
                  {itemMonth.label}
                </option>
              ))}
            </Form.Select>

            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
            <Button variant="primary" onClick={handleDownloadActiveTab} disabled={downloading || loading}>
              {downloading ? "Downloading..." : "Download"}
            </Button>
          </div>
        }
      />

      <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || "overview")} className="mb-4 dashboard-tabs">
        <Tab eventKey="overview" title="Overview Activities" />
        <Tab eventKey="budget" title="Budget" />
        <Tab eventKey="completion" title="Activity Completion" />
        <Tab eventKey="absorption" title="Funds Absorption" />
      </Tabs>

      {loading ? <LoadingState label="Loading dashboard..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && stats && activeTab === "overview" ? (
        <>
          <div className="dashboard-overview-card-grid mb-4">
            <StatCard
              label="Total activities"
              value={formatInteger(stats.total_activities)}
              accent="blue"
              className="stat-card-compact"
            />
            {overviewImplementorCards.map((item, index) => (
              <StatCard
                key={item.key}
                label={item.name}
                value={formatInteger(item.total)}
                accent={["green", "gold", "rose", "blue", "gold"][index] || "blue"}
                className="stat-card-compact"
              />
            ))}
          </div>

          <Row className="g-4">
            <Col xs={12}>
              {renderStackedQuarterChart(
                "overview-pillar-quarter-analysis",
                overviewRows.pillar,
                "Pillar Quarter Analysis",
                "Top pillars by total scheduled activities, broken down across Q1 to Q4.",
                { labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>

            <Col xl={6}>
              {renderStackedQuarterChart(
                "overview-implementor-quarter-analysis",
                overviewRows.implementor,
                "Implementor Quarter Analysis",
                "Top implementors by activity volume across the selected quarter mix."
              )}
            </Col>

            <Col xl={6}>
              {renderStackedQuarterChart(
                "overview-grant-quarter-analysis",
                overviewRows.grant,
                "Grant Quarter Analysis",
                "Top grants by total activities, with quarter contribution shown in each bar."
              )}
            </Col>

            <Col xs={12}>
              {renderSummaryTable(overviewRows.pillar, "Pillar Quarter Summary")}
            </Col>

            <Col xs={12}>
              {renderSummaryTable(overviewRows.implementor, "Implementor Quarter Summary")}
            </Col>

            <Col xs={12}>
              {renderSummaryTable(overviewRows.grant, "Grant Quarter Summary")}
            </Col>
          </Row>
        </>
      ) : null}

      {!loading && stats && activeTab === "budget" ? (
        <>
          <div className="dashboard-overview-card-grid mb-4">
            <StatCard
              label="Total Budget (USD)"
              value={formatCurrency(stats.budget.total_budget_usd)}
              accent="blue"
              className="stat-card-compact"
            />
            {budgetImplementorCards.map((item, index) => (
              <StatCard
                key={item.key}
                label={item.name}
                value={formatCurrency(item.total)}
                accent={["green", "gold", "rose", "blue", "gold"][index] || "blue"}
                className="stat-card-compact"
              />
            ))}
          </div>

          <Row className="g-4">
            <Col xs={12}>
              {renderStackedQuarterChart(
                "budget-pillar-quarter",
                budgetRows.pillar,
                "Budget by Pillar and Quarter",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact, labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "budget-implementor-quarter",
                budgetRows.implementor,
                "Budget by Implementor and Quarter",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "budget-grant-quarter",
                budgetRows.grant,
                "Budget by Grant and Quarter",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xs={12}>{renderSummaryTable(budgetRows.pillar, "Budget by Pillar", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(budgetRows.implementor, "Budget by Implementor", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(budgetRows.grant, "Budget by Grant", formatCurrency)}</Col>
          </Row>
        </>
      ) : null}

      {!loading && stats && activeTab === "completion" ? (
        <>
          <Row className="g-4 mb-4">
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Total Activity-Quarter Schedules" value={formatInteger(stats.completion.total_schedules)} accent="blue" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Completed" value={formatInteger(stats.completion.total_completed)} accent="green" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="In Progress" value={formatInteger(stats.completion.total_ongoing)} accent="gold" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="On hold" value={formatInteger(stats.completion.total_on_hold)} accent="rose" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Not Done" value={formatInteger(stats.completion.total_cancelled)} accent="gold" />
            </Col>
          </Row>

          <Row className="g-4">
            <Col xl={6}>
              {renderStackedQuarterChart(
                "completion-status-by-quarter",
                completionRows,
                "Activity Status by Quarter",
                "Status distribution for scheduled activities across Q1 to Q4.",
                { labelMaxLength: 18, chartHeight: 340 }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedStatusChart(
                "completion-pillar-status",
                completionGroupRows.pillar,
                "Completion by Pillar and Status",
                "Scheduled activities grouped by pillar across the selected statuses.",
                { labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>

            <Col xl={6}>
              {renderStackedStatusChart(
                "completion-implementor-status",
                completionGroupRows.implementor,
                "Completion by Responsible Organisation and Status",
                "Scheduled activities grouped by responsible organisation across the selected statuses."
              )}
            </Col>

            <Col xl={6}>
              {renderStackedStatusChart(
                "completion-grant-status",
                completionGroupRows.grant,
                "Completion by Grant and Status",
                "Scheduled activities grouped by grant across the selected statuses."
              )}
            </Col>

            <Col xs={12}>{renderSummaryTable(completionRows, "Activity Completion by Status")}</Col>

            <Col xs={12}>
              {renderStatusSummaryTable(completionGroupRows.pillar, "Completion by Pillar and Status")}
            </Col>

            <Col xs={12}>
              {renderStatusSummaryTable(completionGroupRows.implementor, "Completion by Responsible Organisation and Status")}
            </Col>

            <Col xs={12}>
              {renderStatusSummaryTable(completionGroupRows.grant, "Completion by Grant and Status")}
            </Col>
          </Row>
        </>
      ) : null}

      {!loading && stats && activeTab === "absorption" ? (
        <>
          <Row className="g-4 mb-4">
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Committed funds" value={formatCurrency(stats.funds_absorption.total_committed)} accent="blue" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Expended funds" value={formatCurrency(stats.funds_absorption.total_expended)} accent="green" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Accounted funds" value={formatCurrency(stats.funds_absorption.total_accounted)} accent="gold" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Absorption rate" value={formatPercent(stats.funds_absorption.absorption_rate)} accent="rose" />
            </Col>
            <Col sm={6} lg={4} xl={2}>
              <StatCard label="Grant balance" value={formatCurrency(stats.funds_absorption.total_balance)} accent="gold" />
            </Col>
          </Row>

          <Row className="g-4">
            <Col xs={12}>
              {renderStackedQuarterChart(
                "absorption-expended-pillar-quarter",
                absorptionRows.pillar,
                "Expended Funds by Pillar and Quarter",
                "Expended funds grouped by pillar and quarter.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact, labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-expended-implementor-quarter",
                absorptionRows.implementor,
                "Expended Funds by Implementor and Quarter",
                "Expended funds by implementor across the selected period.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-expended-grant-quarter",
                absorptionRows.grant,
                "Expended Funds by Grant and Quarter",
                "Expended funds by grant with quarter-level contribution.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xs={12}>
              {renderStackedQuarterChart(
                "absorption-accounted-pillar-quarter",
                accountedRows.pillar,
                "Accounted Funds by Pillar and Quarter",
                "Accounted funds grouped by pillar and quarter.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact, labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-accounted-implementor-quarter",
                accountedRows.implementor,
                "Accounted Funds by Implementor and Quarter",
                "Accounted funds by implementor across the selected period.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-accounted-grant-quarter",
                accountedRows.grant,
                "Accounted Funds by Grant and Quarter",
                "Accounted funds by grant with quarter-level contribution.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xs={12}>
              {renderStackedQuarterChart(
                "absorption-balance-pillar-quarter",
                balanceRows.pillar,
                "Grant Balance by Pillar and Quarter",
                "Grant balance grouped by pillar and quarter.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact, labelMaxLength: 26, chartHeight: 340 }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-balance-implementor-quarter",
                balanceRows.implementor,
                "Grant Balance by Implementor and Quarter",
                "Grant balance by implementor across the selected period.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xl={6}>
              {renderStackedQuarterChart(
                "absorption-balance-grant-quarter",
                balanceRows.grant,
                "Grant Balance by Grant and Quarter",
                "Grant balance by grant with quarter-level contribution.",
                { valueFormatter: formatCurrency, axisFormatter: formatCompact }
              )}
            </Col>
            <Col xs={12}>{renderSummaryTable(absorptionRows.pillar, "Expended Funds by Pillar", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(absorptionRows.implementor, "Expended Funds by Implementor", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(absorptionRows.grant, "Expended Funds by Grant", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(accountedRows.pillar, "Accounted Funds by Pillar", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(accountedRows.implementor, "Accounted Funds by Implementor", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(accountedRows.grant, "Accounted Funds by Grant", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(balanceRows.pillar, "Grant Balance by Pillar", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(balanceRows.implementor, "Grant Balance by Implementor", formatCurrency)}</Col>
            <Col xs={12}>{renderSummaryTable(balanceRows.grant, "Grant Balance by Grant", formatCurrency)}</Col>
          </Row>
        </>
      ) : null}
    </div>
  );
}

export default DashboardPage;
